<template>
    <AppLayout>
        <div class="max-w-7xl mx-auto px-4 py-6">
            <!-- breadcrumb -->
            <Breadcrumb
                :title="$tc('LBL_RETAILER_SALES')"
                :items="[{ label: $tc('LBL_INVOICE') }]"
                :home-label="$t('LBL_HOME')"
                :home-url="route('landing')"
            />
            <!-- breadcrumb end -->

            <div
                class="w-full bg-white dark:bg-gray-900 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 overflow-overflow"
            >
                <!-- Search & Action Bar -->
                <div class="flex flex-wrap items-center justify-between gap-4 p-4 sm:flex-nowrap">
                    <!-- Search Box -->
                    <div class="relative w-full sm:w-auto">
                        <label
                            for="table-search"
                            class="sr-only"
                            >Search</label
                        >
                        <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <svg
                                class="w-5 h-5 text-gray-500 dark:text-gray-400"
                                fill="currentColor"
                                viewBox="0 0 20 20"
                            >
                                <path
                                    fill-rule="evenodd"
                                    d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z"
                                    clip-rule="evenodd"
                                />
                            </svg>
                        </div>
                        <t-input
                            id="retailer-sale-search-box"
                            v-model="search"
                            type="text"
                            variant="success"
                            class="w-full sm:w-80 pl-10 pr-4 py-2.5 text-sm rounded-lg border border-gray-300 focus:ring-1 focus:ring-green-500 focus:outline-none dark:bg-gray-800 dark:border-gray-600 dark:text-white"
                            placeholder="Search for user"
                            @keypress.enter="fetchPageData()"
                        />
                    </div>
                </div>

                <div
                    class="w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-none shadow-md"
                >
                    <div class="overflow-x-auto">
                        <table
                            class="min-w-[700px] w-full table-auto text-sm sm:text-base text-left text-gray-700 dark:text-gray-300"
                        >
                            <thead class="bg-gray-100 dark:bg-gray-700 text-sm font-semibold">
                                <tr>
                                    <!-- <th class="px-4 py-3">#</th> -->
                                    <th class="px-4 py-3">RETAILER</th>
                                    <th class="px-4 py-3">QUANTITY SOLD</th>
                                    <th class="px-4 py-3">SALES AMOUNT</th>
                                </tr>
                            </thead>
                            <tbody>
                                <template v-for="(row, rowIndex) in retailerBills.data">
                                    <tr
                                        :class="[
                                            'border-b border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 transition duration-200',
                                            row.credit ? 'bg-green-200' : '',
                                        ]"
                                        @click="goto('reports.retailer.orders', { retailer: bills[0].user.id })"
                                    >
                                        <!-- <td class="px-4 py-3">{{ rowIndex + 1 + (currentPage - 1) * perPage }}</td> -->
                                        <td class="px-4 py-3">
                                            {{ row[0].user.name }}
                                        </td>
                                        <td class="px-4 py-3">
                                            {{ sumQuantityFromBills(row) }}
                                        </td>
                                        <td class="px-4 py-3">
                                            {{ config(common.currency) }} {{ sumAmountFromBills(row) }}
                                        </td>
                                    </tr>
                                </template>
                                <tr
                                    v-if="!retailerBills.data.length"
                                    class="text-center"
                                >
                                    <td
                                        colspan="6"
                                        class="px-4 py-4 text-gray-500 dark:text-gray-400"
                                    >
                                        {{ $t('LBL_DATA_FOUND') }}
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                    <div
                        v-if="retailerBills.data.length"
                        class="flex justify-center items-center bg-gray-100 dark:bg-gray-800 px-4 py-4 border-t dark:border-gray-700"
                    >
                        <t-pagination
                            v-model="currentPage"
                            variant="rounded"
                            :total-items="totalRows"
                            :per-page="perPage"
                            :limit="limit"
                            :disabled="!retailerBills.data.length"
                            @change="fetchPageData"
                        />
                    </div>
                    <p class="sm:hidden text-xs text-center text-gray-500 py-2">
                        ↔ Swipe left/right to scroll the table
                    </p>
                </div>
            </div>
        </div>
    </AppLayout>
</template>

<script>
    import _ from 'lodash';
    import AppLayout from '../../Layouts/AppLayout.vue';
    import common from '../../common';
    import Breadcrumb from '../../Components/Breadcrumb.vue';

    export default {
        name: 'RetailerReport',

        components: {
            AppLayout,
            Breadcrumb,
        },

        props: {
            retailerBills: {
                type: Object,
                required: true,
            },
            oldSearch: {
                type: String,
                default: null,
            },
        },

        data() {
            return {
                common,
                search: this.oldSearch,
                currentPage: null,
                limit: 5,
                perPage: this.retailerBills.per_page,
                totalRows: this.retailerBills.total,
            };
        },

        computed: {
            disabled() {
                return !this.retailerBills.data.length;
            },
        },

        watch: {
            search: {
                handler() {
                    this.debounceFetchSearchResults();
                },
            },
        },

        mounted() {
            this.debounceFetchSearchResults = _.debounce(this.fetchSearchResults, 1000);
            document.getElementById('retailer-sale-search-box').focus();
        },

        created() {
            this.currentPage = this.retailerBills.current_page;
        },

        methods: {
            sumAmountFromTransactions(transactions) {
                let sumGrandTotal = 0;
                transactions.forEach((transaction) => {
                    sumGrandTotal += parseFloat(transaction.grand_total);
                });
                return sumGrandTotal.toFixed(2);
            },

            sumAmountFromBills(bills) {
                let sumGrandTotal = 0;
                bills.forEach((bill) => {
                    sumGrandTotal += parseFloat(this.sumAmountFromTransactions(bill.transactions));
                });
                return sumGrandTotal.toFixed(2);
            },

            sumQuantityFromTransactions(transactions) {
                let sumQty = 0;
                transactions.forEach((transaction) => {
                    sumQty += transaction.qty;
                });
                return sumQty;
            },

            sumQuantityFromBills(bills) {
                let sumQty = 0;
                bills.forEach((bill) => {
                    sumQty += this.sumQuantityFromTransactions(bill.transactions);
                });
                return sumQty;
            },

            fetchPageData(newPage) {
                this.$inertia.get(
                    route('reports.retailer', {
                        page: newPage,
                        search: this.search,
                    }),
                );
            },

            fetchSearchResults() {
                this.$inertia.get(
                    route('reports.retailer', {
                        page: this.currentPage,
                        search: this.search,
                    }),
                );
            },
        },
    };
</script>
